import type { EventFilter, TelemetryEvent } from '@ringba/core/telemetry';
import { maskIdSegment } from './idSegments';

/**
 * Returns the page a URL shows, named the same in both portals.
 *
 * Rules:
 * - V2's hash route wins.
 * - Otherwise the URL path from `basePath` on, so a prefix before it never reaches the key.
 * - Without a hash route or a match for `basePath`, the key is `/`.
 * - A segment with a digit becomes `?`.
 */
export function pageKey(url: string, basePath?: string): string {
  let path: string;

  try {
    const { hash, pathname } = new URL(url);
    path = hash.startsWith('#/') ? hash.slice(1).split('?')[0] : pathFromBase(pathname, basePath);
  } catch {
    return '/';
  }

  return `/${toSegments(path).map(maskIdSegment).join('/')}`;
}

/** Names every event's view after its {@link pageKey}. */
export function nameViewsByPage<T extends TelemetryEvent>(basePath?: string): EventFilter<T> {
  return (event) => {
    event.view.name = pageKey(event.view.url, basePath);
    return true;
  };
}

// --- Private ---

function pathFromBase(pathname: string, basePath: string | undefined): string {
  const base = toSegments(basePath ?? '');
  if (base.length === 0) return '/';

  const segments = toSegments(pathname);
  const start = segments.findIndex((_, i) => base.every((part, j) => segments[i + j] === part));
  return start < 0 ? '/' : segments.slice(start).join('/');
}

function toSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}
